/**
 * Formatação de exibição (UI): centavos → pt-BR, minutos → hh:mm, datas dd/mm/aaaa.
 * O domínio trafega centavos/minutos inteiros; a conversão acontece só na borda.
 */

/** 123456 → "1.234,56" (pt-BR, 2 casas). */
export function fmtMoeda(centavos: number | null | undefined): string {
  if (centavos === null || centavos === undefined) return "—";
  return (centavos / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** 90 → "1:30"; -25 → "-0:25". */
export function fmtHhMm(minutos: number | null | undefined): string {
  if (minutos === null || minutos === undefined) return "—";
  const sinal = minutos < 0 ? "-" : "";
  const abs = Math.abs(minutos);
  return `${sinal}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, "0")}`;
}

/** "1:30" ou "90:00" (aceita sinal) → minutos inteiros. Erro se inválido. */
export function parseHhMm(texto: string): number {
  const m = /^(-?)(\d{1,4}):([0-5]\d)$/.exec(texto.trim());
  if (!m) throw new Error(`Hora inválida: "${texto}" (use hh:mm, ex.: 1:30)`);
  const total = Number(m[2]) * 60 + Number(m[3]);
  return m[1] === "-" ? -total : total;
}

/** "1.234,56" ou "1234,56" → centavos inteiros. Erro se inválido. */
export function parseMoeda(texto: string): number {
  const limpo = texto.trim().replace(/\./g, "").replace(",", ".");
  if (!/^-?\d+(\.\d{1,2})?$/.test(limpo)) throw new Error(`Valor inválido: "${texto}" (use 1.234,56)`);
  return Math.round(Number(limpo) * 100);
}

/** ISO "2026-06-30T00:00:00.000Z" ou "2026-06-30" → "30/06/2026". */
export function fmtData(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("pt-BR", { timeZone: "UTC" });
}

/** ISO com hora → "30/06/2026 15:41". */
export function fmtDataHora(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString("pt-BR", { timeZone: "UTC" })} ${d.toLocaleTimeString("pt-BR", { timeZone: "UTC", hour: "2-digit", minute: "2-digit" })}`;
}

/** 202606 → "06/2026". */
export function fmtCompetencia(comp: number): string {
  return `${String(comp % 100).padStart(2, "0")}/${Math.floor(comp / 100)}`;
}

/** Rótulos amigáveis de estado da competência. */
export const ROTULO_ESTADO: Record<string, string> = {
  aberta: "Aberta",
  em_calculo: "Em cálculo",
  conferida: "Conferida",
  fechada: "Fechada",
  retificada: "Retificada",
};

/** Rótulos dos tipos de lançamento variável da folha mensal. P1: férias e
 *  médias de 13º NÃO são mais lançamentos — têm configuração persistente nos
 *  processamentos (páginas Férias e 13º). */
export const ROTULO_TIPO_LANCAMENTO: Record<string, string> = {
  he50: "HE 50%",
  he100: "HE 100%",
  adicional_noturno: "Adicional noturno",
  comissao: "Comissão",
  bonus: "Bônus",
  dias_descontados: "Dias descontados",
  // 05/09/2026 (pedido do dono) — evento Domínio 380: indenizatório, fora das bases
  abono_cct: "Abono CCT (indenizatório)",
};

/** Rótulos dos tipos de cálculo da competência/execução. */
export const ROTULO_TIPO_CALCULO: Record<string, string> = {
  mensal: "Folha mensal",
  adiantamento_13: "13º — 1ª parcela",
  decimo_terceiro: "13º — 2ª parcela",
  ferias: "Férias",
};

/** P1 — rótulos dos tipos de processamento (documentos de férias/13º). */
export const ROTULO_TIPO_PROCESSAMENTO: Record<string, string> = {
  ferias: "Férias",
  decimo_primeira: "13º — 1ª parcela",
  decimo_segunda: "13º — 2ª parcela",
};

/** P1 — rótulos de estado do processamento. */
export const ROTULO_ESTADO_PROCESSAMENTO: Record<string, string> = {
  aberto: "Aberto",
  calculado: "Calculado",
  fechado: "Fechado",
};
